/**
 * EditContactDialog
 * -----------------
 * Full contact profile editor for a hospital department contact.
 * Mirrors the Hospital User profile fields (name, title, dept, facility,
 * office/mobile phone, LinkedIn, role, notes) and includes an "Invite to
 * portal" action that promotes the contact into a real Hospital Portal user
 * via `POST /health-systems/{hs_id}/contacts/{contact_id}/invite`.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import DepartmentSelect from "@/components/admin/DepartmentSelect";
import { Loader2, Save, Trash2, Send, CheckCircle, Copy } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PORTAL_ROLES = [
  { value: "staff",            label: "Staff" },
  { value: "manager",          label: "Manager" },
  { value: "authorized_agent", label: "Authorized Agent" },
  { value: "director",         label: "Director" },
];

export default function EditContactDialog({
  open, onOpenChange, hospital, contact, token, onSaved,
}) {
  const hospitalId = hospital?.id;
  const facilities = hospital?.facilities || [];

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [setupLink, setSetupLink] = useState(null);

  useEffect(() => {
    if (!open || !contact) return;
    setForm({
      name: contact.name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      mobile: contact.mobile || "",
      title: contact.title || "",
      department: contact.department || "Supply Chain",
      facility_id: contact.facility_id || "",
      role: contact.role || "staff",
      linkedin: contact.linkedin || "",
      is_primary: !!contact.is_primary,
      notes: contact.notes || "",
    });
    setSetupLink(null);
  }, [open, contact]);

  const submit = async () => {
    if (!form.email?.trim()) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/health-systems/${hospitalId}/contacts/${contact.id}`,
        {
          name: form.name || null,
          email: form.email.trim().toLowerCase(),
          phone: form.phone || null,
          mobile: form.mobile || null,
          title: form.title || null,
          department: form.department || null,
          facility_id: form.facility_id || null,
          role: form.role || null,
          linkedin: form.linkedin || null,
          is_primary: !!form.is_primary,
          notes: form.notes || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Updated ${form.name || form.email}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = "Update failed";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d.msg || "").join("; ");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${contact.name || contact.email} from contacts?`)) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/api/health-systems/${hospitalId}/contacts/${contact.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Contact removed");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const inviteToPortal = async () => {
    // Save any pending edits first so the invite carries the latest info
    await submit();
    setInviting(true);
    try {
      const r = await axios.post(
        `${API}/api/health-systems/${hospitalId}/contacts/${contact.id}/invite`,
        { facility_id: form.facility_id || null, role: form.role || "staff" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = r.data || {};
      setSetupLink(data.setup_link || null);
      if (data.email_sent) {
        toast.success(`Portal invite emailed to ${form.email}`);
      } else {
        toast.message("Setup link ready", {
          description: "SMTP send unverified — copy the link below and email it via Outlook.",
        });
      }
      onSaved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = "Invite failed";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d.msg || "").join("; ");
      toast.error(msg);
    } finally {
      setInviting(false);
    }
  };

  if (!contact) return null;
  const alreadyInvited = !!contact.invited_to_portal || !!contact.portal_user_id;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving && !deleting && !inviting) onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-contact-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Edit contact
            {alreadyInvited && (
              <Badge className="bg-emerald-100 text-emerald-800 text-xs gap-1">
                <CheckCircle className="w-3 h-3" /> Portal invited
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Section: Identity */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Identity</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Title / position</Label>
                <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Director of Supply Chain" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input
                type="email"
                value={form.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="edit-contact-email"
              />
            </div>
          </section>

          {/* Section: Contact channels */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Phone / social</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Office phone</Label>
                <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(281) 555-0123" />
              </div>
              <div>
                <Label className="text-xs">Mobile</Label>
                <Input value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="(281) 555-9876" />
              </div>
            </div>
            <div>
              <Label className="text-xs">LinkedIn</Label>
              <Input value={form.linkedin || ""} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder="https://linkedin.com/in/..." />
            </div>
          </section>

          {/* Section: Organization */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Within the hospital</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Department</Label>
                <DepartmentSelect
                  value={form.department || "Supply Chain"}
                  onChange={(v) => setForm({ ...form, department: v })}
                  testId="edit-contact-department"
                />
              </div>
              <div>
                <Label className="text-xs">Facility</Label>
                <Select
                  value={form.facility_id || "_none_"}
                  onValueChange={(v) => setForm({ ...form, facility_id: v === "_none_" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="System-wide / no facility" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">System-wide</SelectItem>
                    {facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Portal role (used if invited to portal)</Label>
              <Select value={form.role || "staff"} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="edit-contact-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PORTAL_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm pt-1">
              <input
                type="checkbox"
                checked={!!form.is_primary}
                onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
                className="accent-emerald-600"
              />
              Primary contact for this hospital
            </label>
          </section>

          {/* Section: Notes */}
          <section>
            <Label className="text-xs">Notes</Label>
            <Textarea
              rows={2}
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Internal notes about this contact (only visible to admins)"
            />
          </section>

          {/* Section: Portal invite */}
          <section className="rounded-lg border border-blue-200 bg-blue-50/60 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-blue-900 mb-0.5">Invite to hospital portal</h4>
                <p className="text-[11px] text-blue-800/80">
                  Promote this contact to a real login user under <strong>{hospital?.name}</strong>. They'll receive a 7-day setup link to choose a password and complete onboarding.
                </p>
              </div>
              <Button
                size="sm"
                onClick={inviteToPortal}
                disabled={inviting || saving || !form.email}
                className="bg-blue-600 hover:bg-blue-700 gap-1.5 whitespace-nowrap"
                data-testid="edit-contact-invite"
              >
                {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {alreadyInvited ? "Resend invite" : "Invite"}
              </Button>
            </div>
            {setupLink && (
              <div className="mt-2 flex items-center gap-2 rounded bg-white border border-blue-300 p-2 text-xs">
                <span className="truncate flex-1 font-mono text-slate-700">{setupLink}</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard?.writeText(setupLink);
                    toast.success("Setup link copied");
                  }}
                  className="gap-1 shrink-0"
                  data-testid="copy-setup-link"
                >
                  <Copy className="w-3 h-3" /> Copy
                </Button>
              </div>
            )}
          </section>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between border-t pt-3 mt-2">
          <Button
            variant="ghost"
            onClick={remove}
            disabled={deleting || saving || inviting}
            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 gap-1.5"
            data-testid="edit-contact-delete"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Remove
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving || deleting || inviting}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving || deleting || inviting}
              className="bg-blue-600 hover:bg-blue-700 gap-1.5"
              data-testid="edit-contact-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
